import { forwardRef, memo, useImperativeHandle, useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

import { HorizontalContainer, Wrapper } from '@american-autonomy/ag-ui';

import LinkButton from 'components/LinkButton';
import DateInput from 'components/inputs/DateInput';

import useDatePicker from './hooks/useDatePicker';
import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { Props } from './types';

import styles from './styles.module.scss';

const DatePicker = memo(
    forwardRef<HTMLInputElement, Props>(
        (
            {
                className = '',
                interval,
                isFutureAllowed = true,
                onChange,
                placeholder,
                testId = TEST_ID,
                value
            },
            ref
        ) => {
            const { CLEAR, SELECT_DATE } = useTranslation();

            const inputRef = useRef<HTMLInputElement | null>(null);
            const popoverRef = useRef<HTMLDivElement | null>(null);

            useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

            const {
                clear,
                containerRef,
                dayPickerInterval,
                disabledDays,
                endMonth,
                handleBlur,
                handleOpen,
                handleSelect,
                handleTextChange,
                inputValue,
                selectedDate
            } = useDatePicker({
                inputRef,
                interval,
                isFutureAllowed,
                onChange,
                popoverRef,
                value
            });

            return (
                <Wrapper className={`${className} ${styles.datePicker}`} testId={testId}>
                    <Wrapper className={styles.inputContainer} ref={containerRef}>
                        <DateInput
                            onBlur={handleBlur}
                            onChange={handleTextChange}
                            onClick={handleOpen}
                            placeholder={placeholder ?? SELECT_DATE}
                            ref={inputRef}
                            testId={`${testId}-input`}
                            value={inputValue ?? ''}
                        />
                    </Wrapper>

                    <div
                        className={styles.dropdown}
                        data-testid={`${testId}-dropdown`}
                        popover='manual'
                        ref={popoverRef}>
                        <DayPicker
                            defaultMonth={selectedDate}
                            disabled={disabledDays}
                            endMonth={endMonth}
                            hidden={dayPickerInterval}
                            mode='single'
                            onSelect={handleSelect}
                            selected={selectedDate}
                            startMonth={interval?.start as Date | undefined}
                        />

                        <HorizontalContainer className={styles.footer}>
                            <LinkButton onClick={clear} text={CLEAR} />
                        </HorizontalContainer>
                    </div>
                </Wrapper>
            );
        }
    )
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
